import { m, type Message } from '@/i18n';
import { readField } from './module-kit';
import { dateFace, faceNamesYear, isLate, parseTimeValue, type DateFace } from './time-value';

/**
 * The DUE date of an item and its row label (the Work list row, 2026-09-15).
 *
 * The due date IS the Schedule module's end (`schedule.endTime`, docs
 * `domain/modules.ts`): one field, one location, named here once so the read
 * layer's SQL extraction and the record's in-memory read agree. The label is a
 * pure formatter over the stored canonical `TimeValue` string and the caller's
 * clock; it reports lateness as a FACT beside the text and decides no colour -
 * a late fact only turns coloured when health adopts it as Delayed
 * (docs/HEALTH_SIGNALS_V2.md), never here.
 *
 * Calendar math is UTC, as the whole time model is (`time-value.ts`: a day
 * target is timezone-agnostic by design).
 */

/** Where the due date lives in `module_data`. */
export const DUE_FIELD = { moduleKey: 'schedule', fieldKey: 'endTime' } as const;

/** The stored due date (the canonical TimeValue string), or null when unset. */
export function dueAtOf(moduleData: Record<string, unknown>): string | null {
  const v = readField(moduleData, DUE_FIELD.moduleKey, DUE_FIELD.fieldKey);
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export interface DueLabel {
  /** "Due today", "Due tomorrow", "Due Sat", "Due 18 Sep", "Due 18 Sep 2027",
   *  "3 d late" - or the grain's own word past the week ("Due W38", "Due Sep",
   *  "Due Q3", "Due 2027"), as a descriptor the row formats. */
  label: Message;
  /** The one fact the time model reports (`isLate`): `now` is past the end. */
  late: boolean;
}

const DAY = 86_400_000;
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

const dayIndex = (ms: number): number => Math.floor(ms / DAY);
const monthName = (month: number): Message => m(`lists.due.monthName.${MONTHS[month - 1]!}`);

/** The far label for a target beyond this week, grain-honest: a day names its
 *  date, a coarser grain names its own token, and the year appears only when
 *  it is not the current one. The face is the target's end (`dateFace`). */
function farLabel(face: DateFace, now: number): Message {
  const withYear = faceNamesYear(face, now);
  const year = String(face.year);
  switch (face.grain) {
    case 'minute':
    case 'day': {
      const args = { day: String(face.day), month: monthName(face.month) };
      return withYear ? m('lists.due.dayYear', { ...args, year }) : m('lists.due.day', args);
    }
    case 'week': {
      const week = String(face.week).padStart(2, '0');
      return withYear ? m('lists.due.weekYear', { week, year }) : m('lists.due.week', { week });
    }
    case 'month': {
      const month = monthName(face.month);
      return withYear ? m('lists.due.monthYear', { month, year }) : m('lists.due.month', { month });
    }
    case 'quarter': {
      const quarter = String(face.quarter);
      return withYear
        ? m('lists.due.quarterYear', { quarter, year })
        : m('lists.due.quarter', { quarter });
    }
    case 'year':
      return m('lists.due.year', { year });
  }
}

/**
 * The row's due label for a stored due date at `now` (epoch ms), or null when
 * there is no due date (or it does not parse - an unreadable value is silence,
 * never a crash in a row). Vocabulary, by the calendar distance from today to
 * the target's LAST day: "N d late" once passed, "Due today", "Due tomorrow",
 * the weekday within the next six days, then the grain's far label.
 */
export function dueLabel(dueAt: string | null | undefined, now: number): DueLabel | null {
  if (!dueAt) return null;
  const parsed = parseTimeValue(dueAt);
  if (!parsed.ok) return null;
  const tv = parsed.value;
  // The last calendar day the target covers: an instant is its own day, an
  // interval's end is exclusive, so step back a tick.
  const lastDayMs = tv.endMs === tv.startMs ? tv.endMs : tv.endMs - 1;
  const diff = dayIndex(lastDayMs) - dayIndex(now);
  const late = isLate(tv, now);
  if (diff < 0) return { label: m('lists.due.late', { count: -diff }), late };
  if (diff === 0) return { label: m('lists.due.today'), late };
  if (diff === 1) return { label: m('lists.due.tomorrow'), late };
  if (diff <= 6) {
    const weekday = m(`lists.due.weekdayName.${WEEKDAYS[new Date(lastDayMs).getUTCDay()]!}`);
    return { label: m('lists.due.weekday', { weekday }), late };
  }
  return { label: farLabel(dateFace(tv), now), late };
}

/**
 * The instant a due date sorts by: the END of the target (the last moment the
 * work is still on time), or null when there is none or it does not parse -
 * so a list sorting by Due reads soonest first and sinks the undated rows
 * (list-columns.ts `sortValueOf`, the null-sinks rule).
 */
export function dueInstantOf(dueAt: string | null | undefined): number | null {
  if (!dueAt) return null;
  const parsed = parseTimeValue(dueAt);
  return parsed.ok ? parsed.value.endMs : null;
}

/** The clock-free face of a due date: the canonical end as stored
 *  ("2026-09-18", "2026-W38"), for a render that cannot yet know `now` (the
 *  server, the first client paint - `useNow` in the kit). Never a label. */
export function dueAsStored(dueAt: string): string {
  return (dueAt.split('/').pop() ?? dueAt).slice(0, 10);
}
